// R81 — THE DRAWER THAT LISTS EVERYTHING SHE HAS EVER PAID FOR, AND A DATABASE OUTAGE DREW IT EMPTY.
//
// **FOUND BY RUNNING THE CENSUS THREE SESSIONS KEPT FINDING THE SHAPE OF WITHOUT SWEEPING FOR IT**
// (`scripts/r81-initial-value-census.mjs`). The pointer's own widening is what found it, and it is
// worth writing down because it is a different question from R51..R79's: **not "what does the
// failure path write" but "WHAT DOES THE INITIAL VALUE DRAW, AND CAN SHE TELL THAT APART FROM THE
// TRUTH?"** `useState<ShelfItem[]>([])` draws "she has none of these", and that is a sentence about
// her account rather than about the request.
//
// TWO CALL SITES, ONE ROUTE, AND **BOTH FORGED AN EMPTY ANSWER IN A DIFFERENT SPELLING** — which is
// R47's count-the-copies with the count at two:
//   * `loadRoom`  — `await res.json().catch(() => ({ items: [] }))`, then `catch { setItems([]); }`.
//     A room she opens reads as a shelf she has never put anything on.
//   * THE TALLY  — `catch { return [r, 0]; }` across every room at once, and **that one is worse,
//     because it is not one room's silence but the whole drawer's**: the room LIST itself draws
//     "Birth Chart (0), Tarot (0), Magic Room (0)", which is not a failed read, it is an account
//     with nothing in it. She has no way to tell those apart and no reason to look again.
//
// **AND THE SERVER WAS TELLING THE SAME LIE — THE SIXTH ROUTE FOUND ANSWERING A REFUSAL AS AN EMPTY
// COLLECTION** (after R55's `handleVideoProgress`, R60's `handleRoomBackgrounds` and
// `handleLalaMessages`, R64's `handleCauldronList` and R70's `handleSpellsBrowse`).
// `handleRoomShelf` caught its own database error and answered **HTTP 200 `{ room, items: [] }`**,
// and did the same when `ensureUser` came back empty. No client could ever have seen the
// difference: "she has saved nothing in this room" and "we could not ask" arrived byte-identical.
//
// SO `items` IS `T[] | null` — W5-2/R39's THIRD STATE and R18's distinction: a list that has not
// arrived is not an empty list. A COUNT is the same fact one size down, so the tally answers
// `number | null` rather than `0`.
//
// THE ANSWER SHAPE IS ARGUED PER ROUTE, NOT MADE UNIFORM (R58's ruling, and R70's three-not-four).
// This route sits behind sign-in and can refuse, so a bare `T[] | null` would throw away the two
// facts that differ — but the DRAWER has no room for a sentence per room in a list of rooms, and
// what it needs is exactly "did this land". So the transport hands back the full answer and
// `shelfItemsOr` collapses it for the caller that only needs the list. One reader, two depths.
//
// `doFetch` is a PARAMETER (R46's locked-door shape), so a gate drives every answer this route can
// give with no browser, no session and no database. This module imports NOTHING.

export const roomShelfPath = (room: string) => `/api/rooms/${encodeURIComponent(room)}/shelf`;

// THREE answers, and `sign-in` is real here because the route composes one: `handleRoomShelf`
// answers 401 "Sign in to see your shelf." A `refused` carries the server's own sentence when it
// sent one (R72: a message is the server's word only when it arrives beside `ok: false`).
export type ShelfAnswer<T> =
  | { ok: true; items: T[] }
  | { ok: false; kind: "sign-in" }
  | { ok: false; kind: "refused"; message: string }
  | { ok: false; kind: "offline" };

type Json = { ok?: boolean; message?: string; items?: unknown };

export async function fetchRoomShelf<T>(room: string, doFetch: typeof fetch = fetch): Promise<ShelfAnswer<T>> {
  try {
    const res = await doFetch(roomShelfPath(room), { cache: "no-store" });
    if (res.status === 401) { await res.body?.cancel?.().catch(() => {}); return { ok: false, kind: "sign-in" }; }
    const data = (await res.json().catch(() => null)) as Json | null;
    // **PRESENCE, NOT TRUTHINESS** (R53): an empty array is a perfectly good answer and `[]` is
    // truthy anyway, but a 200 carrying no `items` at all is a malformed one and there is nothing
    // to draw. `ok: false` is the server's refusal and outranks whatever else rode with it.
    if (res.ok && data?.ok !== false && Array.isArray(data?.items)) return { ok: true, items: data.items as T[] };
    return { ok: false, kind: "refused", message: typeof data?.message === "string" ? data.message : "" };
  } catch {
    return { ok: false, kind: "offline" };
  }
}

// FOR THE CALLER THAT ONLY NEEDS THE LIST. **`null` IS NOT `[]` AND THAT IS THE WHOLE ROUND** — a
// surface handed null keeps showing what it had, or says it could not look; it never redraws her
// shelf as empty.
export function shelfItemsOr<T>(answer: ShelfAnswer<T>): T[] | null {
  return answer.ok ? answer.items : null;
}

// THE TALLY, ONE ROOM. A count that could not be taken is `null`, never `0`: `(0)` beside a room
// name is a claim about her account.
export function shelfCountOr<T>(answer: ShelfAnswer<T>): number | null {
  return answer.ok ? answer.items.length : null;
}

// The sentence, derived from the answer rather than typed at the call site (R24's shape). **A
// SUCCESS SAYS NOTHING** — the shelf filling in is the whole answer (R51/R53's ruling). Only a
// failure has something to tell her.
export type ShelfCopy = { signIn: string; failed: string };

// THE HOUSE'S OWN TWO SENTENCES, DECLARED HERE RATHER THAN AT THE SURFACE — `saved-books.ts` and
// `bookmarks.ts` set this precedent, and the sentences belong beside the answers they describe.
//
// **ENGLISH, DELIBERATELY, AND THE REASON IS 14-B6c's OWN LESSON: a lone translated line over an
// untranslated screen is the DEFECT.** Every other word in the shelves drawer — "Your shelves",
// "Everything you've made", the empty-shelf invitation — is bare English JSX, so a catalog row here
// would be the one Spanish sentence on an English panel (R60 ruled the same way for the Ask Lala and
// Card Library surfaces). It moves onto the catalog when the drawer does.
export const SHELF_READ_COPY: ShelfCopy = {
  signIn: "Sign in to see your shelf.",
  failed: "Your shelves could not be read just now. Nothing has been lost — try again in a moment.",
};

export function shelfNote<T>(answer: ShelfAnswer<T>, copy: ShelfCopy): string {
  if (answer.ok) return "";
  if (answer.kind === "sign-in") return copy.signIn;
  if (answer.kind === "refused" && answer.message) return answer.message;
  return copy.failed;
}
